/**
 * WriteoffSlice Sub-Entity Reducers
 * 
 * Reducers for updating nested objects within the writeoff (identifiers, patient, detail).
 */

import { PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { WriteoffIdentifiersView, WriteoffPatientView, WriteoffDetailView } from '../../types';
import { WriteoffSliceState } from '../state';
import { addEditedField, createBackupManager } from '../../../shared/sliceUtils';

// Backup manager for HMR recovery
const backupManager = createBackupManager<{
  writeoff: WriteoffSliceState['currentWriteoff'];
  isEdited: boolean;
  editedFields: string[];
}>('writeoff_backup');

/**
 * Helper to persist state to session storage
 */
function persistToSessionStorage(state: WriteoffSliceState) {
  if (state.currentWriteoff) {
    backupManager.persist({
      writeoff: state.currentWriteoff,
      isEdited: state.isEdited,
      editedFields: state.editedFields,
    });
  }
}

// =============================================================================
// SUB-ENTITY UPDATE REDUCERS
// =============================================================================

/**
 * Update writeoff identifiers
 */
export const updateWriteoffIdentifiers: CaseReducer<
  WriteoffSliceState,
  PayloadAction<Partial<WriteoffIdentifiersView>>
> = (state, action) => {
  if (state.currentWriteoff) {
    state.currentWriteoff.identifiers = {
      ...state.currentWriteoff.identifiers,
      ...action.payload,
    };
    addEditedField(state, 'identifiers');
    persistToSessionStorage(state);
  }
};

/**
 * Update writeoff patient information
 */
export const updateWriteoffPatient: CaseReducer<
  WriteoffSliceState,
  PayloadAction<Partial<WriteoffPatientView>>
> = (state, action) => {
  if (state.currentWriteoff) {
    state.currentWriteoff.patient = {
      ...state.currentWriteoff.patient,
      ...action.payload,
    };
    addEditedField(state, 'patient');
    persistToSessionStorage(state);
  }
};

/**
 * Update writeoff detail information
 */
export const updateWriteoffDetail: CaseReducer<
  WriteoffSliceState,
  PayloadAction<Partial<WriteoffDetailView>>
> = (state, action) => {
  if (state.currentWriteoff) {
    state.currentWriteoff.detail = {
      ...state.currentWriteoff.detail,
      ...action.payload,
    };
    addEditedField(state, 'detail');
    persistToSessionStorage(state);
  }
};
